import type { ChatActivityEvent, ChatEvent } from '@wundo/shared/chat.ts';
import type { ChatActivityStep, ChatActivityStepState } from '@wundo/ui';

type Steps = readonly ChatActivityStep[];

export interface ChatTurnActivity {
  /** By conversation: the steps taken since Wundo last began saying something there. */
  pending: ReadonlyMap<string, Steps>;
  /** By message: the steps taken before that reply began. Never read back from history. */
  pinned: ReadonlyMap<string, Steps>;
}

export const NO_TURN_ACTIVITY: ChatTurnActivity = {
  pending: new Map(),
  pinned: new Map(),
};

/**
 * Follows one event through. A step is a row from the moment it starts, and
 * changes in place when it ends — wherever it stands by then. A reply that
 * begins takes the steps before it along, so what comes after belongs to
 * whatever Wundo says next, in the same turn or not.
 */
export function nextTurnActivity(
  state: ChatTurnActivity,
  event: ChatEvent,
): ChatTurnActivity {
  switch (event.type) {
    case 'typing':
      return withPending(state, event.conversation_id, []);

    case 'activity': {
      if (event.phase === 'start') {
        const held = state.pending.get(event.conversation_id) ?? [];
        if (held.some((step) => step.id === event.step_id)) {
          return state;
        }

        return withPending(state, event.conversation_id, [
          ...held,
          stepOf(event, 'running'),
        ]);
      }

      return endStep(state, event.conversation_id, stepOf(event, event.phase));
    }

    case 'delta':
      return pinPending(state, event.conversation_id, event.message_id);

    case 'message':
      if (event.message.author !== 'wundo' || event.message.notice) {
        return state;
      }

      return pinPending(state, event.message.conversation_id, event.message.id);
  }
}

function stepOf(
  event: ChatActivityEvent,
  state: ChatActivityStepState,
): ChatActivityStep {
  return {
    id: event.step_id,
    text: event.text,
    state,
    icon: event.icon,
    ...(event.detail ? { detail: event.detail } : {}),
    ...(event.link ? { link: event.link } : {}),
  };
}

function endStep(
  state: ChatTurnActivity,
  conversationId: string,
  ended: ChatActivityStep,
): ChatTurnActivity {
  const replace = (steps: Steps) =>
    steps.map((step) => (step.id === ended.id ? ended : step));

  const held = state.pending.get(conversationId) ?? [];
  if (held.some((step) => step.id === ended.id)) {
    return withPending(state, conversationId, replace(held));
  }

  for (const [messageId, steps] of state.pinned) {
    if (steps.some((step) => step.id === ended.id)) {
      const pinned = new Map(state.pinned);
      pinned.set(messageId, replace(steps));
      return { ...state, pinned };
    }
  }

  return state;
}

function pinPending(
  state: ChatTurnActivity,
  conversationId: string,
  messageId: string,
): ChatTurnActivity {
  const held = state.pending.get(conversationId) ?? [];

  if (held.length === 0) {
    return state;
  }

  const pinned = new Map(state.pinned);
  pinned.set(messageId, [...(pinned.get(messageId) ?? []), ...held]);

  return { ...withPending(state, conversationId, []), pinned };
}

function withPending(
  state: ChatTurnActivity,
  conversationId: string,
  steps: Steps,
): ChatTurnActivity {
  if (steps.length === 0 && !state.pending.has(conversationId)) {
    return state;
  }

  const pending = new Map(state.pending);
  if (steps.length === 0) {
    pending.delete(conversationId);
  } else {
    pending.set(conversationId, steps);
  }

  return { ...state, pending };
}
